/**
 * Scroll-Triggered Animations
 * Shelly Strips - Midnight Amber Theme
 *
 * Uses Intersection Observer API to trigger animations when elements enter viewport.
 * Respects prefers-reduced-motion for accessibility.
 */

(function() {
  'use strict';

  // Check if user prefers reduced motion
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Skip all animations if reduced motion is preferred
  if (prefersReducedMotion) {
    return;
  }

  // Configuration
  const config = {
    // Threshold: 0-1, percentage of element visible before triggering
    threshold: 0.15,
    // Root margin: trigger animations slightly before element enters viewport
    rootMargin: '0px 0px -50px 0px',
    // Mobile: simpler animations on smaller screens
    isMobile: window.innerWidth < 768
  };

  /**
   * Intersection Observer callback
   * Adds 'is-visible' class when element enters viewport
   */
  const observerCallback = (entries, observer) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');

        // Handle staggered children
        if (entry.target.hasAttribute('data-stagger-children')) {
          handleStaggeredChildren(entry.target);
        }

        // Unobserve after animation (one-time reveal)
        // Comment out if you want animations to repeat on scroll
        observer.unobserve(entry.target);
      }
    });
  };

  /**
   * Handle staggered animation for child elements
   */
  const handleStaggeredChildren = (parent) => {
    const children = parent.querySelectorAll('[data-stagger-item]');
    const delay = parseInt(parent.getAttribute('data-stagger-delay')) || 100;

    children.forEach((child, index) => {
      setTimeout(() => {
        child.classList.add('is-visible');
      }, index * delay);
    });
  };

  /**
   * Sequential reveal for elements that should appear in order
   */
  const handleSequentialReveal = (elements) => {
    const delay = 400; // ms between reveals

    elements.forEach((element, index) => {
      setTimeout(() => {
        element.classList.add('is-visible');
      }, index * delay);
    });
  };

  // Create the Intersection Observer
  const observer = new IntersectionObserver(observerCallback, {
    threshold: config.threshold,
    rootMargin: config.rootMargin
  });

  /**
   * Initialize scroll animations
   */
  const init = () => {
    // Generic scroll reveal elements
    const scrollRevealElements = document.querySelectorAll('[data-scroll-reveal]');
    scrollRevealElements.forEach(element => {
      // Add hidden state initially
      element.classList.add('scroll-reveal');
      observer.observe(element);
    });

    // Format Benefits cards
    const formatBenefitCards = document.querySelectorAll('.format-benefit');
    formatBenefitCards.forEach(card => {
      card.classList.add('scroll-reveal');
      observer.observe(card);
    });

    // Problem/Solution sections (sequential reveal)
    const problemSection = document.querySelector('.problem__content');
    const agitateSection = document.querySelector('.agitate__content');
    const solutionContent = document.querySelector('.solution__content');
    const solutionImage = document.querySelector('.solution__image');

    if (problemSection) {
      problemSection.classList.add('scroll-reveal');
      observer.observe(problemSection);
    }

    if (agitateSection) {
      agitateSection.classList.add('scroll-reveal');
      observer.observe(agitateSection);
    }

    if (solutionContent) {
      solutionContent.classList.add('scroll-reveal');
      observer.observe(solutionContent);
    }

    if (solutionImage) {
      solutionImage.classList.add('scroll-reveal', 'scroll-reveal--slide-left');
      observer.observe(solutionImage);
    }

    // How It Works - steps use CSS animation (fadeIn in how-it-works.css)
    // Not using scroll-reveal for steps to avoid visibility issues
    // Only connectors use scroll-reveal
    const howItWorksConnectors = document.querySelectorAll('.how-it-works__connector');
    howItWorksConnectors.forEach(connector => {
      connector.classList.add('scroll-reveal');
      observer.observe(connector);
    });

    // Testimonials section (fade in as whole)
    const testimonialsHeading = document.querySelector('.testimonials__heading');
    const testimonialsCarousel = document.querySelector('.testimonials__carousel-wrapper');

    if (testimonialsHeading) {
      testimonialsHeading.classList.add('scroll-reveal');
      observer.observe(testimonialsHeading);
    }

    if (testimonialsCarousel) {
      testimonialsCarousel.classList.add('scroll-reveal');
      observer.observe(testimonialsCarousel);
    }

    // Trust & Transparency columns (stagger)
    const trustColumns = document.querySelectorAll('.trust-column');
    trustColumns.forEach(column => {
      column.classList.add('scroll-reveal');
      observer.observe(column);
    });

    const comparisonTable = document.querySelector('.trust-transparency__comparison');
    if (comparisonTable) {
      comparisonTable.classList.add('scroll-reveal');
      observer.observe(comparisonTable);
    }

    // FAQ items
    const faqItems = document.querySelectorAll('.faq__item');
    faqItems.forEach(item => {
      item.classList.add('scroll-reveal');
      observer.observe(item);
    });

    // Ingredient Showcase: image + text (cards use data-stagger-children auto-handling)
    const ingredientShowcaseImage = document.querySelector('.ingredient-showcase__image-wrapper');
    if (ingredientShowcaseImage) {
      ingredientShowcaseImage.classList.add('scroll-reveal', 'scroll-reveal--scale');
      observer.observe(ingredientShowcaseImage);
    }

    const ingredientShowcaseText = document.querySelector('.ingredient-showcase__text');
    if (ingredientShowcaseText) {
      ingredientShowcaseText.classList.add('scroll-reveal', 'scroll-reveal--slide-right');
      observer.observe(ingredientShowcaseText);
    }

    // Ingredient Showcase: stagger container for cards
    const ingredientGrid = document.querySelector('.ingredient-showcase__grid[data-stagger-children]');
    if (ingredientGrid) {
      observer.observe(ingredientGrid);
    }

    // Pricing card
    const pricingCard = document.querySelector('.pricing__card');
    if (pricingCard) {
      pricingCard.classList.add('scroll-reveal', 'scroll-reveal--scale');
      observer.observe(pricingCard);
    }

    // Final CTA section
    const finalCTA = document.querySelector('.final-cta');
    if (finalCTA) {
      finalCTA.classList.add('scroll-reveal');
      observer.observe(finalCTA);
    }
  };

  /**
   * Scroll progress indicator for hero
   */
  const initScrollIndicator = () => {
    const hero = document.querySelector('.hero');
    if (!hero) return;

    // Create scroll indicator
    const indicator = document.createElement('div');
    indicator.className = 'scroll-indicator';
    indicator.setAttribute('aria-hidden', 'true');
    indicator.innerHTML = `
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M12 5v14M19 12l-7 7-7-7"/>
      </svg>
    `;
    hero.appendChild(indicator);

    // Hide on scroll
    let scrollTimeout;
    const handleScroll = () => {
      const scrollY = window.scrollY || window.pageYOffset;

      if (scrollY > 100) {
        indicator.classList.add('hidden');
      } else {
        indicator.classList.remove('hidden');
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    // Add click handler to scroll down
    indicator.addEventListener('click', () => {
      const viewportHeight = window.innerHeight;
      window.scrollTo({
        top: viewportHeight,
        behavior: 'smooth'
      });
    });
  };

  /**
   * Optional: Scroll snapping between major sections
   * Uncomment to enable
   */
  const initScrollSnapping = () => {
    // Add scroll-snap to body or main container
    const main = document.querySelector('main');
    if (main) {
      main.style.scrollSnapType = 'y proximity';

      // Add snap points to major sections
      const sections = document.querySelectorAll('section');
      sections.forEach(section => {
        section.style.scrollSnapAlign = 'start';
        section.style.scrollSnapStop = 'normal';
      });
    }
  };

  /**
   * Handle page visibility changes
   * Pause observers when tab is not visible
   */
  const handleVisibilityChange = () => {
    if (document.hidden) {
      observer.disconnect();
    } else {
      init();
    }
  };

  document.addEventListener('visibilitychange', handleVisibilityChange);

  /**
   * Re-initialize on dynamic content changes
   * Useful for Shopify section rendering
   */
  window.reinitScrollAnimations = () => {
    observer.disconnect();
    init();
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      init();
      initScrollIndicator();
      // Uncomment to enable scroll snapping:
      // initScrollSnapping();
    });
  } else {
    init();
    initScrollIndicator();
    // Uncomment to enable scroll snapping:
    // initScrollSnapping();
  }

  /**
   * Handle resize events
   * Update config and re-initialize if needed
   */
  let resizeTimeout;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(() => {
      const wasMobile = config.isMobile;
      config.isMobile = window.innerWidth < 768;

      // Re-initialize if crossing mobile/desktop threshold
      if (wasMobile !== config.isMobile) {
        observer.disconnect();
        init();
      }
    }, 250);
  }, { passive: true });

  // Expose API for debugging
  if (window.location.hostname === 'localhost' || window.location.hostname.includes('127.0.0.1')) {
    window.ScrollAnimations = {
      observer,
      config,
      reinit: init,
      version: '1.0.0'
    };
    console.log('Scroll Animations initialized. Access via window.ScrollAnimations');
  }

})();
